import { ActionIcon, Flex, Grid, Group, Stack, Title } from '@mantine/core';
import Button, { ButtonTypes } from '@/components/Buttons/Button';
import { IconFlag, IconX } from '@tabler/icons-react';
import { addRefParam, isDark, timeForToday } from '@/utils/utils';
import { useAtomValue, useSetAtom } from 'jotai';

import BookmarkButton from '@/components/Buttons/BookmarkButton';
import CustomImage from '@/components/CustomImage';
import DMText from '@/components/DMText';
import Header from './Header';
import Link from 'next/link';
import LoadingSkeleton from './LoadingSkeleton';
import { QueryClient } from '@tanstack/react-query';
import QuizBody from './QuizBody';
import QuizBox from './QuizBox';
import ShareButton from '@/components/Buttons/ShareButton';
import SimilarContents from './SimilarContents';
import TextBody from './TextBody';
import VideoBody from './VideoBody';
import VoteBody from './VoteBody';
import dynamic from 'next/dynamic';
import { images } from '@/utils/images';
import { isModalNumber } from '@/features/Discovery/discovery.state';
import { mixpanelTrack } from '@/utils/mixpanel';
import themeVariables from '@/styles/themeVariables';
import { useDiscoveryContentDetailModalStyle } from '@/features/Discovery/discovery.style';
import { useFooterData } from '../hook/useFooterMethod';
import { useGetContentDetail } from '../hook/api';
import { useModalControl } from '../hook/useHeaderMethod';
import { useRouter } from 'next/navigation';

const LikeButton = dynamic(() => import('@/components/Buttons/LikeButton'), {
  ssr: false,
});

const ModalBody = () => {
  const queryClient = new QueryClient();

  const isModal = useAtomValue(isModalNumber);
  const checkModal = isModal !== 0;

  const { dark } = isDark();
  const { push } = useRouter();
  const { classes } = useDiscoveryContentDetailModalStyle({ dark });

  const { closeModal } = useModalControl();
  const { data, isInitialLoading } = useGetContentDetail();

  const products = {
    quiz: <QuizBody id={data?.content?.type_id} />,
    vote: <VoteBody id={data?.content?.type_id} />,
    text: <TextBody />,
    video: <VideoBody />,
  };

  const handleDiscoveryButton = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['discoverys'],
    });
    push(`/discoveries`);
  };

  if (isInitialLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className={classes.modalContainer}>
      <Grid columns={12} gutter={0} className={classes.modalBody}>
        <Grid.Col
          md={8}
          className={classes.contentContainer}
          sx={{ paddingTop: checkModal ? 12 : 0 }}
        >
          {checkModal && <Header />}
          <Title
            order={1}
            sx={{
              fontSize: 21,
              fontWeight: 600,
              color: themeVariables.darkMode.strong(dark),
            }}
          >
            {data?.content?.title
              .replaceAll('&lt;', '<')
              .replaceAll('&gt;', '>')}
          </Title>
          {products[data?.content?.type as keyof typeof products]}
          <ButtonGroup classes={classes} data={data} />
          <QuizBox id={data?.content?.id} />
        </Grid.Col>
        <Grid.Col md={4} className={classes.sideContainer}>
          {checkModal ? (
            <ActionIcon
              className={classes.headerCloseButtonInSide}
              onClick={closeModal}
            >
              <IconX color={themeVariables.darkMode.normal(dark)} />
            </ActionIcon>
          ) : (
            <Button
              type={ButtonTypes.gray}
              style={(theme) => ({
                width: '100%',
                fontWeight: 'bold',
                [theme.fn.smallerThan('sm')]: {
                  marginBottom: 20,
                },
              })}
              onClick={handleDiscoveryButton}
            >
              디스커버리 목록 가기
            </Button>
          )}
          <Stack
            className={classes.sideContainerInner}
            sx={{
              paddingTop: checkModal ? 12 : 24,
            }}
          >
            <SidePanel classes={classes} data={data} checkModal={checkModal} />
            {checkModal && <SimilarContents />}
          </Stack>
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default ModalBody;

const ButtonGroup = ({ classes, data }) => {
  const { dark } = isDark();
  const { onClickBookMarkButton, onClickLikeButton } = useFooterData();
  const { openReportModal } = useModalControl();

  return (
    <Flex className={classes.actionButtonContainer}>
      <LikeButton
        id={data?.content?.id}
        active={data?.content?.is_like}
        color="#EC4134"
        onClick={onClickLikeButton}
        className={classes.actionButton}
        label={'좋아요'}
        size={21}
      />
      <BookmarkButton
        size={21}
        active={data?.content?.is_bookmarked}
        onClick={onClickBookMarkButton}
        label={'북마크'}
        className={classes.actionButton}
      />
      <ShareButton
        title={data?.content?.title}
        text={`${data?.content?.summary.slice(0, 40)}...`}
        url={
          process.env.NEXT_PUBLIC_BASE_URL + `/discoveries/${data?.content?.id}`
        }
        tooltip={false}
        className={classes.actionButton}
        label={'공유하기'}
        size={21}
      />
      <Group
        className={classes.actionButton}
        onClick={openReportModal}
        sx={{ border: 'none' }}
      >
        <ActionIcon size={36}>
          <IconFlag size={21} color={themeVariables.darkMode.normal(dark)} />
        </ActionIcon>
        <DMText
          dColor={themeVariables.darkMode.normal}
          sx={(theme) => ({
            [theme.fn.smallerThan('sm')]: {
              display: 'none',
            },
          })}
        >
          신고하기
        </DMText>
      </Group>
    </Flex>
  );
};

const SidePanel = ({ classes, data, checkModal }) => {
  const { push, back } = useRouter();

  const setModal = useSetAtom(isModalNumber);

  const handleProfile = () => {
    const username = data?.content?.user.username || '';

    if (['오늘의 토픽', '데일리 퀴즈', '코드너리'].includes(username)) {
      return;
    }

    if (checkModal) {
      back();
      setModal(0);
      setTimeout(() => {
        push(`/user-profile/detail/${username}`);
      }, 300);
      return;
    }

    setModal(0);
    push(`/user-profile/detail/${username}`);
  };

  return (
    <>
      <Flex
        align={'center'}
        mb={4}
        gap={10}
        sx={(theme) => ({
          [theme.fn.smallerThan('sm')]: {
            marginTop: 8,
          },
        })}
      >
        {data?.content?.url &&
          ['video', 'text'].includes(data?.content?.type || '') && (
            <Link
              href={addRefParam(data?.content?.url)}
              target="_blank"
              rel="ugc"
            >
              <Button
                type={ButtonTypes.brightColored}
                onClick={() => {
                  mixpanelTrack(
                    'DiscoveryDetailModal_OpenUrlButton',
                    'Clicked',
                    {
                      id: data?.content?.id,
                    }
                  );
                }}
              >
                지금 바로 {data?.content?.type == 'text' ? '읽기' : '보기'}
              </Button>
            </Link>
          )}
        <div className={classes.headerFooterContainer}>
          <DMText
            size={13}
            weight={400}
            dColor={themeVariables.darkMode.neutral}
          >
            {~~((data?.content?.duration || 0) / 1000 / 60) || 1}분 분량
          </DMText>
          <DMText
            size={13}
            weight={400}
            dColor={themeVariables.darkMode.neutral}
          >
            •
          </DMText>
          <DMText
            size={13}
            weight={400}
            dColor={themeVariables.darkMode.neutral}
          >
            조회수 {data?.content?.content_count.view}
          </DMText>
        </div>
      </Flex>
      <div className={classes.headerUserInfoContainer}>
        <div onClick={handleProfile}>
          <DMText
            size={14}
            weight={600}
            dColor={themeVariables.darkMode.strong}
          >
            <CustomImage
              width={21}
              height={21}
              className={classes.headerUserProfileImage}
              src={data?.content?.user.image_url}
              alt="profile-image"
              errSrc={images.defaultProfile}
            />
            {data?.content?.user.username}
          </DMText>
        </div>
        <DMText size={13} weight={400} dColor={themeVariables.darkMode.neutral}>
          • {timeForToday(data?.content?.created_at)}
        </DMText>
      </div>
    </>
  );
};
